import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SearchIcon from "@mui/icons-material/Search";
import {
  Button,
  Chip,
  FormControl,
  Select,
  Tooltip,
  useTheme,
} from "@mui/material";
import MenuItem from "@mui/material/MenuItem";
import type { Theme } from "@mui/material/styles";
import type React from "react";

import { formatMoney } from "../../formatters";
import { PREDEFINED_BANK_TRANSACTION_FILTERS } from "../../status";
import { enumToStrings, isoToDate } from "../../utils/utils";
import { BankTransactionStatus, BankTransactionType } from "../bankMatch";
import { NavigationCell } from "../ClaimsTable/ui/NavigationCell";
import type { CellParams, DataTableColDef } from "../DataTable";
import InProgressIndicator from "../InProgressIndicator";
import { renderCellRedacted } from "../redact";

function renderTransactionSelect(props: {
  value: string;
  emptyLabel: string;
  options: Array<{ value: string; label?: string }>;
  onChange: (value: string) => void;
  renderCustomValue?: (selected: string) => React.ReactNode;
  theme: Theme;
}): JSX.Element {
  const { value, emptyLabel, options, onChange, renderCustomValue, theme } =
    props;

  return (
    <FormControl fullWidth>
      <Select
        displayEmpty
        value={value}
        renderValue={(selected) => {
          if (!value) {
            return (
              <em style={{ color: theme.palette.text.disabled }}>
                {emptyLabel}
              </em>
            );
          }

          if (renderCustomValue) {
            return renderCustomValue(selected as string);
          }

          return selected as string;
        }}
        onChange={(e) => onChange(e.target.value as string)}
        sx={{
          ".MuiOutlinedInput-notchedOutline": {
            border: value ? 0 : `1px solid ${theme.palette.error.main}`,
          },
          borderRadius: "12px",
        }}
      >
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label || option.value}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

type TransactionCellParams = CellParams<BankTransactionMessage>;

function TransactionTypeSelect({
  params,
  transactionUpdate,
}: {
  params: TransactionCellParams;
  transactionUpdate: (transactionUpdate: BankTransactionUpdateMessage) => void;
}): JSX.Element {
  const theme = useTheme();
  const value = params.value as string;
  const transaction = params.row;

  if (transaction.status === "MATCHED") {
    return (
      <div style={{ padding: "8px 14px", color: theme.palette.text.primary }}>
        {value}
      </div>
    );
  }

  return renderTransactionSelect({
    value,
    emptyLabel: "Select type",
    theme,
    options: enumToStrings(BankTransactionType).map((option) => ({
      value: option,
    })),
    onChange: (newValue) => {
      const { wieldyId, practiceId } = transaction;
      transactionUpdate({
        wieldyId,
        practiceId,
        transactionType: newValue as keyof typeof BankTransactionType,
      });
    },
  });
}

const STATUS_OPTIONS = enumToStrings(BankTransactionStatus).map((option) => {
  let label: string = option;

  switch (option) {
    case "AUTO_MATCHED":
      label = "Auto Matched";
      break;
    case "MANUALLY_MATCHED":
      label = "Manually Matched";
      break;
    case "POSTED_OUTSIDE":
      label = "Posted Outside Copilot";
      break;
    case "MISSING":
      label = "Report Missing";
      break;
    case "DUPLICATE":
      label = "Duplicate";
      break;
    case "REVERSED":
      label = "Reversed";
      break;
    default:
      label = option;
  }

  return {
    value: option,
    label,
  };
});

const DEVMODE_STATUSES = new Set(["DUPLICATE", "REVERSED"]);

function formatDateShort(isoDate: string): string {
  try {
    const date = new Date(isoDate);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return isoDate;
  }
}

function formatDuration(startIso: string, endIso: string): string {
  const start = new Date(startIso);
  const end = new Date(endIso);
  const diffMs = end.getTime() - start.getTime();
  if (diffMs < 0) return "";

  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const diffHours = Math.floor(
    (diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
  );

  if (diffDays > 0) {
    return `${diffDays}d ${diffHours}h`;
  }
  if (diffHours > 0) {
    return `${diffHours}h`;
  }
  return "< 1h";
}

function getStatusChipProps(
  status: string,
  theme: Theme,
): { label: string; sx: Record<string, string> } {
  switch (status) {
    case "AUTO_MATCHED":
      return {
        label: "Auto Matched",
        sx: {
          backgroundColor: theme.palette.success.light,
          color: theme.palette.success.contrastText,
        },
      };
    case "MANUALLY_MATCHED":
      return {
        label: "Manually Matched",
        sx: {
          backgroundColor: theme.palette.success.light,
          color: theme.palette.success.contrastText,
        },
      };
    case "POSTED_OUTSIDE":
      return {
        label: "Posted Outside Copilot",
        sx: {
          backgroundColor: theme.palette.grey[200],
          color: theme.palette.text.primary,
        },
      };
    case "MISSING":
      return {
        label: "Reported Missing",
        sx: {
          backgroundColor: theme.palette.error.light,
          color: theme.palette.error.contrastText,
        },
      };
    case "DUPLICATE":
      return {
        label: "Duplicate",
        sx: {
          backgroundColor: theme.palette.warning.light,
          color: theme.palette.warning.contrastText,
        },
      };
    case "REVERSED":
      return {
        label: "Reversed",
        sx: {
          backgroundColor: theme.palette.warning.light,
          color: theme.palette.warning.contrastText,
        },
      };
    default:
      return {
        label: status || "—",
        sx: {
          backgroundColor: theme.palette.grey[100],
          color: theme.palette.text.secondary,
        },
      };
  }
}

function buildStatusTooltip(
  status: string,
  transaction: BankTransactionMessage,
): string | null {
  const isMatched = status === "AUTO_MATCHED" || status === "MANUALLY_MATCHED";

  if (status === "MISSING" && transaction.reportedMissingAt) {
    return `Reported missing on ${formatDateShort(transaction.reportedMissingAt)}`;
  }

  if (isMatched) {
    const lines: string[] = [];
    if (transaction.matchedAt) {
      lines.push(`Matched on ${formatDateShort(transaction.matchedAt)}`);
    }
    if (transaction.reportedMissingAt && transaction.matchedAt) {
      const duration = formatDuration(
        transaction.reportedMissingAt,
        transaction.matchedAt,
      );
      if (duration) {
        lines.push(`Previously missing — resolved in ${duration}`);
      }
    } else if (transaction.reportedMissingAt) {
      lines.push(
        `Previously reported missing on ${formatDateShort(transaction.reportedMissingAt)}`,
      );
    }
    if (lines.length > 0) return lines.join("\n");
  }

  return null;
}

function renderStatusBadge(
  status: string,
  transaction: BankTransactionMessage,
  theme: Theme,
): JSX.Element {
  const chipProps = getStatusChipProps(status, theme);
  const tooltip = buildStatusTooltip(status, transaction);

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "4px",
        padding: "8px 0",
      }}
    >
      <Chip label={chipProps.label} size="small" sx={chipProps.sx} />
      {tooltip && (
        <Tooltip
          title={<span style={{ whiteSpace: "pre-line" }}>{tooltip}</span>}
        >
          <InfoOutlinedIcon
            fontSize="small"
            sx={{ color: theme.palette.text.secondary, cursor: "pointer" }}
          />
        </Tooltip>
      )}
    </div>
  );
}

function StatusSelect({
  params,
  transactionUpdate,
  onFindMatches,
  selectedFilter,
  devMode,
}: {
  params: TransactionCellParams;
  transactionUpdate: (transactionUpdate: BankTransactionUpdateMessage) => void;
  onFindMatches?: (transaction: BankTransactionMessage) => void;
  selectedFilter?: PREDEFINED_BANK_TRANSACTION_FILTERS;
  devMode?: boolean;
}): JSX.Element {
  const theme = useTheme();
  const value = params.value as string;
  const transaction = params.row;
  const isReview =
    selectedFilter === PREDEFINED_BANK_TRANSACTION_FILTERS.REVIEW;

  // On Pending and All tabs, render all statuses as read-only badges
  if (!isReview) {
    if (!value) {
      return <div style={{ padding: "8px 14px" }}>—</div>;
    }
    return renderStatusBadge(value, transaction, theme);
  }

  // --- Review tab: interactive controls ---

  // Read-only badge for already-resolved statuses
  if (value === "AUTO_MATCHED" || value === "MANUALLY_MATCHED") {
    return renderStatusBadge(value, transaction, theme);
  }

  // OTHER type is non-actionable on Review tab
  if (transaction.transactionType === "OTHER") {
    return (
      <div style={{ padding: "8px 14px", color: theme.palette.text.secondary }}>
        {value || "—"}
      </div>
    );
  }

  // Check if transaction is eligible for finding matches
  // Eligible: INSURANCE type, no payment_key, status is null or not resolved
  const isEligibleForMatching =
    transaction.transactionType === "INSURANCE" &&
    !transaction.paymentKey &&
    (!value ||
      (value !== "POSTED_OUTSIDE" &&
        value !== "MISSING" &&
        value !== "AUTO_MATCHED" &&
        value !== "MANUALLY_MATCHED"));

  // Show disabled "Find Matches" when type is unknown (null)
  const isUnknownType = !transaction.transactionType;

  if (isUnknownType && !value && onFindMatches) {
    return (
      <Tooltip title="Select transaction type first">
        <span style={{ width: "100%" }}>
          <Button
            type="button"
            variant="outlined"
            color="primary"
            disabled
            startIcon={<SearchIcon fontSize="small" />}
            style={{
              cursor: "default",
              width: "100%",
            }}
          >
            Find Matches
          </Button>
        </span>
      </Tooltip>
    );
  }

  if (isEligibleForMatching && onFindMatches) {
    return (
      <Button
        type="button"
        variant="outlined"
        color="primary"
        startIcon={<SearchIcon fontSize="small" />}
        style={{
          cursor: "pointer",
          width: "100%",
        }}
        onClick={() => {
          onFindMatches(transaction);
        }}
      >
        Find Matches
      </Button>
    );
  }

  return renderTransactionSelect({
    value,
    emptyLabel: "Select status",
    theme,
    options: STATUS_OPTIONS.filter(
      (option) =>
        option.value !== "AUTO_MATCHED" &&
        option.value !== "MANUALLY_MATCHED" &&
        (devMode || !DEVMODE_STATUSES.has(option.value)),
    ),
    onChange: (selectedValue) => {
      const { wieldyId, practiceId } = transaction;
      transactionUpdate({
        wieldyId,
        practiceId,
        status: selectedValue as keyof typeof BankTransactionStatus,
      });
    },
    renderCustomValue: (selected) => {
      const selectedOption = STATUS_OPTIONS.find(
        (option) => option.value === selected,
      );
      return selectedOption ? selectedOption.label : selected;
    },
  });
}

export function getDefaultColumns(
  practiceValueOptions: string[],
  searchParams: URLSearchParams,
  transactionUpdate: (transactionUpdate: BankTransactionUpdateMessage) => void,
  onFindMatches?: (transaction: BankTransactionMessage) => void,
  onUnmatchTransaction?: (wieldyId: string) => Promise<void>,
  devMode?: boolean,
  selectedFilter?: PREDEFINED_BANK_TRANSACTION_FILTERS,
  pendingIds?: Set<string>,
): DataTableColDef<BankTransactionMessage>[] {
  const isReview =
    selectedFilter === PREDEFINED_BANK_TRANSACTION_FILTERS.REVIEW;
  const isAll = selectedFilter === PREDEFINED_BANK_TRANSACTION_FILTERS.ALL;

  const columns: DataTableColDef<BankTransactionMessage>[] = [
    {
      flex: 0.75,
      field: "practiceDisplayName",
      minWidth: 150,
      headerName: "Practice",
      type: "singleSelect",
      valueOptions: practiceValueOptions.length
        ? practiceValueOptions
        : undefined,
      filterable: false,
    },
    {
      flex: 0.75,
      type: "date",
      field: "bankDate",
      minWidth: 115,
      headerName: "Bank Date",
      valueGetter: (value) => (value ? isoToDate(value as string) : ""),
    },
    {
      flex: 1.5,
      field: "description",
      minWidth: 220,
      headerName: "Description",
      renderCell: (props) => renderCellRedacted(props),
    },
    {
      flex: 0.75,
      type: "number",
      field: "amount",
      minWidth: 110,
      headerName: "Amount",
      valueFormatter: (value) =>
        value === null ? "-" : formatMoney(value as number),
    },
    {
      flex: 0.75,
      field: "bankName",
      minWidth: 150,
      headerName: "Bank Name",
    },
    ...(devMode
      ? [
          {
            flex: 0.75,
            field: "wieldyMerchantName",
            minWidth: 150,
            headerName: "Merchant",
            valueFormatter: (value: unknown) => (value as string | null) ?? "-",
          },
          {
            flex: 0.75,
            field: "payerLabel",
            minWidth: 150,
            headerName: "Payer",
            filterable: false,
            sortable: false,
            valueFormatter: (value: unknown) => (value as string | null) ?? "-",
          },
        ]
      : []),
    {
      flex: 1,
      type: "singleSelect",
      field: "transactionType",
      minWidth: 170,
      headerName: "Transaction Type",
      valueOptions: enumToStrings(BankTransactionType),
      renderCell: (props) => (
        <TransactionTypeSelect
          params={props}
          transactionUpdate={transactionUpdate}
        />
      ),
    },
    {
      flex: 1,
      field: "monetId",
      minWidth: 150,
      headerName: "Monet Id",
      renderCell: ({ row }) =>
        row.bankPaymentId ? <span>{row.bankPaymentId}</span> : <span>-</span>,
    },
    {
      flex: 1,
      field: "paymentKey",
      minWidth: 150,
      headerName: "Payment Id",
      renderCell: ({ value, row }) => {
        if (value) {
          return (
            <NavigationCell
              linkPath={`/payments/${value}?${searchParams}`}
              displayText={row.payerPaymentId || String(value)}
              ariaLabel="View payment details"
            />
          );
        }
        return <div>-</div>;
      },
    },
    {
      flex: 0.75,
      type: "date",
      field: "matchedAt",
      minWidth: 115,
      headerName: "Matched At",
      valueGetter: (value) => (value ? new Date(value as string) : null),
    },
    {
      flex: 1.5,
      type: "singleSelect",
      field: "status",
      minWidth: 200,
      headerName: "Status",
      valueOptions: STATUS_OPTIONS,
      renderCell: (props) => {
        if (pendingIds?.has(props.row.wieldyId)) {
          return <InProgressIndicator />;
        }
        return (
          <StatusSelect
            params={props}
            transactionUpdate={transactionUpdate}
            onFindMatches={onFindMatches}
            selectedFilter={selectedFilter}
            devMode={devMode}
          />
        );
      },
    },
  ];

  // Add actions column only in dev mode for matched transactions or those with resetable status
  if (devMode && onUnmatchTransaction) {
    columns.push({
      flex: 1,
      field: "unmatch",
      minWidth: 130,
      headerName: "Actions",
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        const isMatched = Boolean(params.row.paymentKey);
        const hasResetableStatus =
          params.row.status === "POSTED_OUTSIDE" ||
          params.row.status === "MISSING";
        const isPending = Boolean(pendingIds?.has(params.row.wieldyId));
        const canUnmatch = (isMatched || hasResetableStatus) && !isPending;

        return (
          <Button
            disabled={!canUnmatch}
            type="button"
            variant="contained"
            style={{
              cursor: canUnmatch ? "pointer" : "default",
              width: "100%",
            }}
            onClick={() => {
              if (canUnmatch) {
                onUnmatchTransaction(params.row.wieldyId);
              }
            }}
          >
            {isMatched ? "Unmatch" : "Reset"}
          </Button>
        );
      },
    });
  }

  return columns.filter((col) => {
    if (col.field === "monetId" && !isReview) return false;
    if (col.field === "paymentKey" && !isAll) return false;
    return true;
  });
}
